/**
 * Cart Goal Validator
 * Monitors cart changes and removes free products if their unlock criteria are no longer met
 * Prevents abuse where customers add products to unlock gifts, then remove them while keeping the gift
 */

class CartGoalValidator {
  constructor() {
    this.STRAPLESS_BRA_HANDLE = 'free-strapless-push-up-bra';
    this.LAUNDRY_BAG_HANDLE = 'free-delicate-laundry-bag';
    this.isProcessing = false;
    this._validateTimer = null;

    this.onCartChange = this.onCartChange.bind(this);
    this.validateCart = this.validateCart.bind(this);
  }

  init() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('cart:updated', this.onCartChange);

    // Poll every 5 seconds as a safety net
    this.validationInterval = setInterval(this.validateCart, 5000);
  }

  onCartChange(event) {
    // Skip validation if this was triggered by our own removal
    if (event && event.detail && event.detail.baseEvent === 'cart-goal-removal') {
      return;
    }
    // Debounce: clear any pending validation, wait 500ms after last event
    clearTimeout(this._validateTimer);
    this._validateTimer = setTimeout(this.validateCart, 500);
  }

  async validateCart() {
    if (this.isProcessing) return;

    try {
      this.isProcessing = true;
      const cart = await this.fetchCart();
      if (!cart || !cart.items) return;

      const braRemoved = await this.validateStraplessBra(cart);
      const bagRemoved = await this.validateLaundryBag(cart);

      if (braRemoved || bagRemoved) {
        // Let the theme re-render the cart drawer cleanly
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      // Silent fail
    } finally {
      this.isProcessing = false;
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.json');
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async validateStraplessBra(cart) {
    const braInCart = cart.items.some(item => item.handle === this.STRAPLESS_BRA_HANDLE);
    if (!braInCart) return false;

    const { cartTotalUSD } = this.getCartTotals(cart);
    if (cartTotalUSD < 90) {
      return await this.removeProductFromCart(this.STRAPLESS_BRA_HANDLE, cart);
    }
    return false;
  }

  async validateLaundryBag(cart) {
    const bagInCart = cart.items.some(item => item.handle === this.LAUNDRY_BAG_HANDLE);
    if (!bagInCart) return false;

    const braInCart = cart.items.some(item => item.handle === this.STRAPLESS_BRA_HANDLE);
    const { cartTotalUSD } = this.getCartTotals(cart);

    if (cartTotalUSD < 120 || !braInCart) {
      return await this.removeProductFromCart(this.LAUNDRY_BAG_HANDLE, cart);
    }
    return false;
  }

  getCartTotals(cart) {
    const cartTotalLocal = cart.total_price / 100;
    const exchangeRate = this.getExchangeRateToUSD();
    const cartTotalUSD = exchangeRate !== 1 ? cartTotalLocal / exchangeRate : cartTotalLocal;
    return { cartTotalLocal, cartTotalUSD, exchangeRate };
  }

  getExchangeRateToUSD() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        if (window.Shopify.currency.active === 'USD') return 1;
        return window.Shopify.currency.rate || 1;
      }
    } catch (e) {}
    return 1;
  }

  /**
   * Remove a product from cart by handle.
   * Uses the already-fetched cart data to avoid an extra /cart.json request.
   */
  async removeProductFromCart(handle, cart) {
    try {
      const lineItem = cart.items.find(item => item.handle === handle);
      if (!lineItem) return false;

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineItem.key, quantity: 0 })
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  destroy() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('cart:updated', this.onCartChange);
    clearTimeout(this._validateTimer);
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartGoalValidator = new CartGoalValidator();
    window.cartGoalValidator.init();
  });
} else {
  window.cartGoalValidator = new CartGoalValidator();
  window.cartGoalValidator.init();
}
